import { makeAutoObservable, runInAction } from 'mobx';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import {
  ACTIVITY_LOG_FILTER_OPTIONS_QUERY,
  ActivityLogEntry,
  ActivityLogExport,
  ActivityLogFilterInput,
  ActivityLogFilterOptions,
  ActivityLogSortField,
  EXPORT_GLOBAL_ACTIVITY_LOG_MUTATION,
  GLOBAL_ACTIVITY_LOG_QUERY,
  PaginatedActivityLog,
} from '@graphql';
import { getCodeError, graphqlApi } from '@utils';
import { store } from '@store';
import {
  exportFileName,
  isValidCustomRange,
  resolveTimeRange,
  TimePeriod,
  toCsvRows,
  UtcRange,
} from '../../utils/activity-log';

export interface ActivityLogFiltersState {
  orgIds: string[] | null;
  includeNoOrganization: boolean;
  superadminIds: string[] | null;
  includeSystem: boolean;
  actions: string[];
  timePeriod: TimePeriod;
  customStart: string | null;
  customEnd: string | null;
  search: string;
}

const DEFAULT_FILTERS: ActivityLogFiltersState = {
  orgIds: null,
  includeNoOrganization: true,
  superadminIds: null,
  includeSystem: true,
  actions: [],
  timePeriod: 'last30Days',
  customStart: null,
  customEnd: null,
  search: '',
};

const EMPTY_OPTIONS: ActivityLogFilterOptions = { organizations: [], administrators: [], actions: [] };

const EXPORT_TOO_LARGE = 'ACTIVITY_LOG_EXPORT_TOO_LARGE';

type ActivityLogGraphqlResponse = {
  data?: {
    errors?: Array<{ message?: string; extensions?: { code?: string } }>;
  };
};

const handleErrors = (response: ActivityLogGraphqlResponse): boolean => {
  const errors = response?.data?.errors;
  if (!errors?.length) return false;

  const { message } = errors[0] || {};
  if (message === 'Unauthorized') store.userStore.clearSession();
  else if (message === 'Forbidden resource') toast('Access denied', { type: 'error' });
  else if (getCodeError(response) === EXPORT_TOO_LARGE) toast(message, { type: 'warning' });
  else toast('Something went wrong try again later', { type: 'error' });
  return true;
};

export class ActivityLogStore {
  filters: ActivityLogFiltersState = { ...DEFAULT_FILTERS };

  sortField: ActivityLogSortField = 'createdAt';

  sortDirection: 'ASC' | 'DESC' = 'DESC';

  page = 0;

  pageSize = 100;

  items: ActivityLogEntry[] = [];

  total = 0;

  options: ActivityLogFilterOptions = EMPTY_OPTIONS;

  isLoading = false;

  isExporting = false;

  // Frozen "now" for relative presets. Re-taken whenever the filters change; page, page-size, sort and export reuse it,
  // so every page and the export of one filter selection share the same time window.
  rangeAnchor: Date = new Date();

  // Identifies the latest fetchPage call; responses from older calls are ignored.
  private requestSeq = 0;

  // Identifies the latest fetchOptions call; responses from older calls are ignored.
  private optionsRequestSeq = 0;

  constructor() {
    makeAutoObservable<ActivityLogStore, 'requestSeq' | 'optionsRequestSeq'>(this, {
      requestSeq: false,
      optionsRequestSeq: false,
    });
  }

  get canQuery(): boolean {
    return this.filters.timePeriod !== 'custom' || isValidCustomRange(this.filters.customStart, this.filters.customEnd);
  }

  get currentRange(): UtcRange {
    return resolveTimeRange(this.filters.timePeriod, this.rangeAnchor, {
      start: this.filters.customStart,
      end: this.filters.customEnd,
    });
  }

  private filterInput(range: UtcRange): ActivityLogFilterInput {
    const { orgIds, includeNoOrganization, superadminIds, includeSystem, actions, search } = this.filters;
    return {
      orgIds,
      includeNoOrganization,
      superadminIds,
      includeSystem,
      actions: actions.length ? actions : null,
      from: range.from,
      to: range.to,
      search: search.trim() || null,
    };
  }

  private get sortInput() {
    return { field: this.sortField, direction: this.sortDirection };
  }

  reset = () => {
    this.filters = { ...DEFAULT_FILTERS };
    this.rangeAnchor = new Date();
    this.sortField = 'createdAt';
    this.sortDirection = 'DESC';
    this.page = 0;
    this.items = [];
    this.total = 0;
    this.options = EMPTY_OPTIONS;
    this.requestSeq += 1;
    this.optionsRequestSeq += 1;
    this.isLoading = false;
  };

  // Called when the page mounts. The store outlives the page, so "now" must be re-taken here,
  // not left at app load or the last time the page was closed.
  open = async () => {
    this.rangeAnchor = new Date();
    await Promise.all([this.fetchOptions(), this.fetchPage()]);
  };

  setFilters = (patch: Partial<ActivityLogFiltersState>) => {
    this.filters = { ...this.filters, ...patch };
    this.rangeAnchor = new Date();
    this.page = 0;
    return this.fetchPage();
  };

  setPage = (page: number) => {
    this.page = page;
    return this.fetchPage();
  };

  setPageSize = (pageSize: number) => {
    this.pageSize = pageSize;
    this.page = 0;
    return this.fetchPage();
  };

  setSort = (field: ActivityLogSortField, direction: 'ASC' | 'DESC') => {
    this.sortField = field;
    this.sortDirection = direction;
    this.page = 0;
    return this.fetchPage();
  };

  fetchPage = async () => {
    this.requestSeq += 1;
    const seq = this.requestSeq;
    const isStale = () => seq !== this.requestSeq;

    if (!this.canQuery) {
      this.items = [];
      this.total = 0;
      this.isLoading = false;
      return;
    }
    this.isLoading = true;
    try {
      const response = await graphqlApi(GLOBAL_ACTIVITY_LOG_QUERY, {
        filter: this.filterInput(this.currentRange),
        sort: this.sortInput,
        pagination: { page: this.page + 1, limit: this.pageSize },
      });
      if (isStale()) return;
      if (handleErrors(response)) return;
      const result: PaginatedActivityLog | undefined = response?.data?.data?.globalActivityLog;
      runInAction(() => {
        this.items = result?.items ?? [];
        this.total = result?.total ?? 0;
      });
    } catch {
      if (!isStale()) toast('Something went wrong try again later', { type: 'error' });
    } finally {
      runInAction(() => {
        if (!isStale()) this.isLoading = false;
      });
    }
  };

  fetchOptions = async () => {
    this.optionsRequestSeq += 1;
    const seq = this.optionsRequestSeq;
    const isStale = () => seq !== this.optionsRequestSeq;

    try {
      const response = await graphqlApi(ACTIVITY_LOG_FILTER_OPTIONS_QUERY);
      if (isStale()) return;
      if (handleErrors(response)) return;
      runInAction(() => {
        this.options = response?.data?.data?.activityLogFilterOptions ?? EMPTY_OPTIONS;
      });
    } catch {
      if (!isStale()) toast('Something went wrong try again later', { type: 'error' });
    }
  };

  exportCsv = async () => {
    if (!this.canQuery || this.isExporting) return;
    const range = this.currentRange;
    this.isExporting = true;
    try {
      const response = await graphqlApi(EXPORT_GLOBAL_ACTIVITY_LOG_MUTATION, {
        filter: this.filterInput(range),
        sort: this.sortInput,
      });
      if (handleErrors(response)) return;
      const result: ActivityLogExport | undefined = response?.data?.data?.exportGlobalActivityLog;
      if (!result) return;
      if (!result.rows.length) {
        toast('No entries match these filters', { type: 'info' });
        return;
      }

      const worksheet = XLSX.utils.json_to_sheet(toCsvRows(result.rows));
      const csv = XLSX.utils.sheet_to_csv(worksheet);
      // The BOM makes Excel on Windows read the UTF-8 file with the right encoding.
      saveAs(new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' }), exportFileName(range, result.rows, new Date()));
    } catch {
      toast('Something went wrong try again later', { type: 'error' });
    } finally {
      runInAction(() => {
        this.isExporting = false;
      });
    }
  };
}
